import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'

// ============================================================
// BOOTSTRAP CONTRACT — how the RN app hands this page a session.
//
// The RN WebView opens a URL shaped like:
//   https://meet.yourapp.com/call
//     ?taskId=...
//     &taskTitle=...                  (used for PDF export naming)
//     &otherPartyName=...             (used for PDF export header — who the session was with)
//     &callType=video|voice
//     &applicationId=...            (optional, pre-hire calls only)
//     &idToken=<firebase id token>
//     &apiBaseUrl=https://staging-api.matchnbook.ca
//     &appCheckToken=<firebase app check token>  (optional — see below)
//     &prefetched=<JSON of the call token response>  (optional — see below)
//
// idToken is a short-lived Firebase ID token, same as
// getIdToken() in the RN app. This page does NOT do its own
// Firebase sign-in — the RN app is already authenticated, so it
// just hands this page a fresh token when it opens the WebView.
// Because the token expires, the RN side should be prepared to
// reload the WebView with a fresh token if the page reports back
// an auth failure (see the 'auth-expired' postMessage in
// videoCallClient.js / whiteboardClient.js).
//
// appCheckToken is the App Check attestation token, fetched the
// same way api.ts's request interceptor gets one natively
// (getAppCheckToken() in config/appCheck.ts) — this page has no
// access to that SDK itself, since it's a sandboxed web page, not
// native code. Optional because App Check may not be enforced, or
// attestation may have failed on-device (see appCheck.ts's own
// fail-open behavior) — api.js only attaches the header when a
// token is actually present, and lets the backend decide whether
// a missing one is fatal. Without this, any request from inside
// this WebView to a non-exempt /api/ route gets a 401 the moment
// APP_CHECK_ENFORCED is true server-side (confirmed in staging:
// see whiteboard export's "[appCheck] BLOCKED — missing token").
//
// `prefetched`, when present, is the exact JSON response the RN
// app's own VideoCallScreen already got back from the call token
// endpoint before ever opening this WebView. When set, App.jsx
// skips calling fetchCallToken itself — without this, every call
// silently fetched a token TWICE (once from RN, once from here),
// each fetch independently recording a CallSession row, firing
// the incoming-call push notification, and consuming one unit of
// the pre-hire call-count limit. See VideoCallScreen.tsx /
// WebCallView.tsx on the RN side for where this gets set.
// ============================================================

const params = new URLSearchParams(window.location.search)

let prefetchedCallInfo
const prefetchedRaw = params.get('prefetched')
if (prefetchedRaw) {
  try {
    prefetchedCallInfo = JSON.parse(prefetchedRaw)
  } catch {
    // Malformed/truncated param — fall back to the normal fetch
    // path rather than crashing the page over an optimization.
    prefetchedCallInfo = undefined
  }
}

const bootstrap = {
  taskId:         params.get('taskId'),
  taskTitle:      params.get('taskTitle') || undefined,
  otherPartyName: params.get('otherPartyName') || undefined,
  callType:       params.get('callType') || 'video',
  applicationId:  params.get('applicationId') || undefined,
  idToken:        params.get('idToken'),
  apiBaseUrl:     params.get('apiBaseUrl'),
  appCheckToken:  params.get('appCheckToken') || undefined,
  prefetchedCallInfo,
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App bootstrap={bootstrap} />
  </React.StrictMode>
)